import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRight, CheckCircle, Briefcase, Users, Code2, Mic, TrendingUp,
  Clock, Lock, Sparkles, DollarSign, Mail, LogIn,
} from "lucide-react";

const AUDIENCES = [
  {
    icon: Briefcase,
    title: "Venture capitalists",
    blurb: "Your portfolio founders need working MVPs before the next round. Send them to us and earn on every paid project.",
    examples: ["Seed & pre-seed funds", "Angel syndicates", "Accelerator partners"],
  },
  {
    icon: Users,
    title: "Startup advisors",
    blurb: "Turn every advisory call into a working prototype. Keep your commission on the build while your client ships faster.",
    examples: ["Fractional CTOs", "Product mentors", "Accelerator coaches"],
  },
  {
    icon: Code2,
    title: "Dev agencies",
    blurb: "Overflow projects? Clients whose budget doesn't fit? Refer them here, collect 15%, keep the relationship for bigger work later.",
    examples: ["Studios at capacity", "Boutique agencies", "White-label partners"],
  },
  {
    icon: Mic,
    title: "Creators & freelancers",
    blurb: "Newsletter, YouTube, X, podcast — audiences of founders convert. One link, 90-day first-touch attribution, monthly payouts.",
    examples: ["Founder newsletters", "No-code / AI creators", "Indie consultants"],
  },
];

const HOW_IT_WORKS = [
  { n: 1, title: "Apply in 60 seconds", desc: "Tell us about your audience. We review within 2 business days." },
  { n: 2, title: "Share your link", desc: "Get a personal tracking URL. Every click is first-touch attributed for 90 days." },
  { n: 3, title: "Earn 15% forever", desc: "On every paid invoice — deposits, final payments, and additional charges." },
];

const FAQ = [
  { q: "How and when am I paid?", a: "Commissions unlock as soon as a referred client pays. Request a payout from your dashboard at any time — we settle manually via bank transfer or PayPal within 5 business days." },
  { q: "What's the attribution window?", a: "90 days from the first click. If the same visitor comes back later, the sale is still credited to you. First-touch wins over last-touch." },
  { q: "Do I need to disclose the referral?", a: "Yes — we require all promoters to clearly disclose the commercial relationship in line with FTC / EU advertising rules. Our promoter guidelines give you ready-to-paste language." },
  { q: "What if my referral buys again?", a: "Every paid invoice from that client within the attribution window earns you commission — including final payments and additional charges on the same project." },
  { q: "Can I promote in multiple channels?", a: "Absolutely. Newsletter, X, YouTube, podcast, blog, LinkedIn — all fair game. The dashboard breaks down which source is converting so you can double down." },
];

function EarningsCalculator() {
  const [referrals, setReferrals] = useState(3);
  const [avgProject, setAvgProject] = useState(6000);
  const monthly = useMemo(() => referrals * avgProject * 0.15, [referrals, avgProject]);
  const annual = monthly * 12;
  return (
    <div className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200 rounded-3xl p-6 sm:p-8" data-testid="earnings-calculator">
      <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-white border border-emerald-200 px-3 py-1 rounded-full mb-4">
        <Sparkles size={10} /> Earnings calculator
      </div>
      <h3 className="text-2xl sm:text-3xl font-extrabold text-zinc-950 tracking-tight mb-2">See what 15% can look like</h3>
      <p className="text-sm text-zinc-600 mb-6">Move the sliders. The math is simple.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <div>
          <label className="block text-xs font-semibold text-zinc-700 mb-2">Paid referrals per month: <span className="text-zinc-950 font-extrabold">{referrals}</span></label>
          <input data-testid="input-calc-referrals" type="range" min={1} max={20} value={referrals} onChange={(e) => setReferrals(Number(e.target.value))}
            className="w-full accent-emerald-600" />
          <div className="flex justify-between text-[10px] text-zinc-400 mt-1"><span>1</span><span>20</span></div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-700 mb-2">Average project size: <span className="text-zinc-950 font-extrabold">${avgProject.toLocaleString()}</span></label>
          <input data-testid="input-calc-project-size" type="range" min={3000} max={15000} step={500} value={avgProject} onChange={(e) => setAvgProject(Number(e.target.value))}
            className="w-full accent-emerald-600" />
          <div className="flex justify-between text-[10px] text-zinc-400 mt-1"><span>$3k</span><span>$15k</span></div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-zinc-200 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Per month</div>
          <div className="text-3xl sm:text-4xl font-extrabold text-zinc-950 mt-1" data-testid="calc-monthly">${Math.round(monthly).toLocaleString()}</div>
        </div>
        <div className="bg-zinc-950 rounded-2xl p-4 text-white">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">Per year</div>
          <div className="text-3xl sm:text-4xl font-extrabold mt-1" data-testid="calc-annual">${Math.round(annual).toLocaleString()}</div>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mt-4">Illustrative only — actual earnings depend on audience fit and conversion.</p>
    </div>
  );
}

export default function Promoters() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white" data-testid="promoters-marketing-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-zinc-200 text-zinc-700 text-xs font-semibold mb-6">
            <Sparkles size={12} className="text-emerald-600" /> Promoter program
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-zinc-950 leading-[1.03]">
            Earn <span className="bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent">15%</span> referring founders
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-2xl leading-relaxed">
            If you talk to founders daily — investors, advisors, agencies, creators — turn those conversations into recurring commission. Send them a working MVP in weeks, keep 15% of every paid invoice.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button data-testid="cta-apply-hero" onClick={() => navigate("/promoter/apply")}
              className="inline-flex items-center justify-center px-6 py-3 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              Apply to become a promoter <ArrowRight size={14} className="ml-2" />
            </button>
            <Link to="/promoter/login" data-testid="cta-existing-promoter-signin"
              className="inline-flex items-center justify-center px-6 py-3 bg-white border border-zinc-200 text-zinc-800 rounded-full text-sm font-semibold hover:border-zinc-400 hover:bg-zinc-50 transition-colors">
              <LogIn size={14} className="mr-2" /> Already a promoter? Sign in
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-8 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1.5"><Clock size={12} /> 90-day attribution</span>
            <span className="inline-flex items-center gap-1.5"><DollarSign size={12} /> Paid on request</span>
            <span className="inline-flex items-center gap-1.5"><TrendingUp size={12} /> Lifetime on repeat invoices</span>
            <span className="inline-flex items-center gap-1.5"><Lock size={12} /> No exclusivity clause</span>
          </div>
        </div>
      </section>

      {/* Who is this for? */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Who this is for</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">If founders listen to you, you'll earn here</h2>
            <p className="mt-3 text-zinc-500 text-sm leading-relaxed">We pay 15% on every paid invoice. No cap, no clawback after 14 days, no exclusivity. Built for four kinds of partners:</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="promoters-audiences">
            {AUDIENCES.map(({ icon: Icon, title, blurb, examples }) => (
              <div key={title} data-testid={`audience-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 hover:border-zinc-300 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center mb-4">
                  <Icon size={18} className="text-zinc-800" />
                </div>
                <h3 className="text-lg font-bold text-zinc-950 mb-2">{title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed mb-3">{blurb}</p>
                <div className="flex flex-wrap gap-2">
                  {examples.map((e) => (
                    <span key={e} className="text-[11px] font-semibold text-zinc-600 bg-white border border-zinc-200 px-2.5 py-1 rounded-full">{e}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-zinc-50">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">How it works</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Three steps. Then you're earning.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="promoters-how-it-works">
            {HOW_IT_WORKS.map(({ n, title, desc }) => (
              <div key={n} className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid={`how-step-${n}`}>
                <div className="w-8 h-8 rounded-full bg-zinc-950 text-white text-xs font-extrabold flex items-center justify-center mb-4">{n}</div>
                <h3 className="text-base font-bold text-zinc-950 mb-2">{title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings calculator */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <EarningsCalculator />
        </div>
      </section>

      {/* What you get */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-zinc-950 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">What you get</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">A real partner dashboard, not a forgotten spreadsheet</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="promoters-benefits">
            {[
              { icon: TrendingUp, title: "Traffic by source", desc: "See which channels, referrers, and landing pages drive your clicks." },
              { icon: Users, title: "Lead pipeline", desc: "Every referred project with live status — quoted, in progress, delivered." },
              { icon: DollarSign, title: "Transparent commissions", desc: "Every invoice, every cut. Pending, paid, exportable." },
              { icon: CheckCircle, title: "One-click payout request", desc: "When you're ready, hit the button. We settle within 5 business days." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white/5 border border-white/10 rounded-2xl p-5 flex gap-4" data-testid={`benefit-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-emerald-300" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">{title}</h3>
                  <p className="text-sm text-zinc-300 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">FAQ</span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mb-8">The details</h2>
          <div className="divide-y divide-zinc-200 border-y border-zinc-200" data-testid="promoters-faq">
            {FAQ.map(({ q, a }) => (
              <details key={q} className="group py-5" data-testid={`faq-${q.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}>
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <h3 className="text-base font-semibold text-zinc-950">{q}</h3>
                  <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none mt-0.5">+</span>
                </summary>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-950">Start earning this week</h2>
          <p className="mt-4 text-zinc-600 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
            Apply in under a minute. We approve fits within 2 business days and you're live with a tracking link and dashboard the same day.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <button data-testid="cta-apply-final" onClick={() => navigate("/promoter/apply")}
              className="inline-flex items-center justify-center px-8 py-4 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              Apply to become a promoter <ArrowRight size={14} className="ml-2" />
            </button>
            <Link to="/promoter/login" data-testid="cta-signin-final"
              className="inline-flex items-center justify-center px-8 py-4 bg-white border border-zinc-200 text-zinc-800 rounded-full text-sm font-semibold hover:border-zinc-400 hover:bg-zinc-50 transition-colors">
              <Mail size={14} className="mr-2" /> Existing promoter? Magic-link sign in
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
